import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { dummyCallback } from '../ComboBoxSettings';

const Wrapper = styled.div`
  position: relative;
  width: ${(props) => props.width || '300px'};
  font-family: sans-serif;
  z-index: 10;
  .ant-input::placeholder {
      color: red;
    }
`;

const Input = styled.input`
  width: 100%;
  height: ${(props) => props.height || '40px'};
  padding: 0 10px;
  font-size: 14px;
  box-sizing: border-box;
  border: 1px solid #ccc;
  border-radius: 5px;
  color: #000;
  outline:none;
`;

const Dropdown = styled.ul`
  position: absolute;
  width: 100%;
  max-height: 200px;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
  background: white;
  border: 1px solid #ccc;
  border-top: none;
  z-index: 10;
  border-radius: 5px;
`;

const Option = styled.li`
  padding: 10px;
  background: ${(props) => (props.highlighted ? '#efefef' : 'white')};
  cursor: pointer;
  color: #000;
  &:hover {
    background: #ddd;
  }
`;

const logicList = [
  { id: '1', name: 'item 1' },
  { id: '2', name: 'item 2' },
  { id: '3', name: 'item 3' },
];

export default function SearchSelect({
  optionsList = logicList,
  onSelect = dummyCallback,
  width = '400px',
  height = '40px',
  placeholder = 'Search or add logic...',
}) {
  const [inputValue, setInputValue] = useState('');
  const [filtered, setFiltered] = useState(optionsList);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selected, setSelected] = useState(null);

  const wrapperRef = useRef(null);
  const optionRefs = useRef([]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        handleBlur();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [highlightedIndex]);

  const handleOptionClick = (option) => {
    setSelected(option);
    setInputValue(option.name);
    setShowDropdown(false);
    setHighlightedIndex(-1);
    onSelect({ id: option.id, name: option.name, type: 'existing' });
  };

  const handleBlur = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      setShowDropdown(false);
      return;
    }
    const match = optionsList.find(
      (item) => item.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (match) {
      handleOptionClick(match);
    } else if (!selected || selected.name !== trimmed) {
      const customItem = { id: null, name: trimmed };
      setSelected(customItem);
      onSelect({ name: trimmed, type: 'new' });
    }
    setShowDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      handleBlur();
      return;
    }
    if (filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setShowDropdown(true);
      setHighlightedIndex((prev) => (prev + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setShowDropdown(true);
      setHighlightedIndex((prev) =>
        (prev - 1 + filtered.length) % filtered.length
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showDropdown && highlightedIndex >= 0 && filtered[highlightedIndex]) {
        handleOptionClick(filtered[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    const results = optionsList.filter((logic) =>
      logic.name.toLowerCase().includes(value.toLowerCase())
    );
    setFiltered(results);
    setHighlightedIndex(results.length > 0 ? 0 : -1);
    setShowDropdown(results.length > 0);
    onSelect({ name: value.trim(), type: 'new' });
  };

  return (
    <Wrapper ref={wrapperRef} width={width} className="searchSelectWrapper">
      <Input
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          if (filtered.length > 0) {
            setShowDropdown(true);
          }
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        height={height}
        placeholder={placeholder}
      />
      {showDropdown && filtered.length > 0 && (
        <Dropdown>
          {filtered.map((logic, idx) => (
            <Option
              key={logic.id}
              ref={(el) => (optionRefs.current[idx] = el)}
              onMouseDown={() => handleOptionClick(logic)}
              highlighted={highlightedIndex === idx}
            >
              {logic.name}
            </Option>
          ))}
        </Dropdown>
      )}
    </Wrapper>
  );
}
